// src/services/agentApi.js
//
// The only module that knows the v2 Decode Agent HTTP contract (frontend
// spec §8.1). Owns URL construction, auth headers, JSON-vs-multipart body
// building, the SSE parse loop, and classification of non-stream outcomes.
// It also owns the read side of durable approvals: the browser must never
// depend on a summary that existed only in one Server-Sent Event frame, so the
// parked-approval records are also subscribed to directly (email spec §7.13).
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db, mainAuth } from './firebase';

// 409 before any stream bytes: another turn holds the lock, or this
// client_message_id is in flight. Always transient (backend §8).
export class BusyError extends Error {
  constructor(retryAfter = null) {
    super('Agent is busy');
    this.name = 'BusyError';
    this.retryAfter = retryAfter;
  }
}

// The backend explains a refused request in its JSON body: FastAPI's
// HTTPException carries `detail`, the service's own error handlers and the
// body-size guard carry `error`. That sentence ("Uploaded files exceed the
// 40MB aggregate limit.") is what the person needs to read; the status number
// alone is only the fallback for a body that carries none (X13-3).
const refusalMessage = (status, bodyText) => {
  let explanation = null;
  try {
    const body = JSON.parse(bodyText);
    explanation = body?.detail ?? body?.error;
  } catch (parseError) {
    // Not JSON — the status alone carries the error.
  }
  if (typeof explanation === 'string' && explanation.trim()) return explanation.trim();
  return `Turn request failed with status ${status}`;
};

// Any non-OK, non-409 response.
export class TurnRequestError extends Error {
  constructor(status, bodyText) {
    super(refusalMessage(status, bodyText));
    this.name = 'TurnRequestError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

const serviceBaseUrl = () => {
  const base = process.env.REACT_APP_DECODE_AGENT_URL;
  if (!base) {
    throw new Error('REACT_APP_DECODE_AGENT_URL is not configured');
  }
  return base;
};

/**
 * POST a turn and consume its SSE stream.
 *
 * Resolves:
 *   { outcome: 'streamed' }          — the stream closed normally
 *   { outcome: 'replayed', result }  — non-stream 200: the turn already completed (§7.4)
 *   { outcome: 'aborted' }           — the signal fired: deliberate, never an error (§8.1)
 * Throws: BusyError (409) | TurnRequestError (other non-OK) | network errors.
 *
 * canvasEdit: { blob, filename, metadata } | null.
 * onEvent(parsedEventData) is called for every SSE data line; unknown event
 * types are passed through (the reducer ignores them).
 */
export async function streamTurn({
  agentId,
  userMessage,
  files = [],
  canvasEdit = null,
  clientMessageId,
  signal,
  onEvent,
}) {
  const url = `${serviceBaseUrl()}/v2/agents/${agentId}/turns`;

  const idToken = await mainAuth.currentUser.getIdToken();
  const emulatedOrg = sessionStorage.getItem('emulatedOrganisation');
  const headers = { 'Authorization': `Bearer ${idToken}` };
  if (emulatedOrg) headers['X-Emulated-Organisation'] = emulatedOrg;

  // Body identical in shape to v1 (assistant_id moves to the URL path;
  // client_message_id is new and required).
  let body;
  if (files.length > 0 || canvasEdit) {
    const formData = new FormData();
    formData.append('user_message', userMessage);
    formData.append('client_message_id', clientMessageId);
    files.forEach((file) => formData.append('files', file));
    if (canvasEdit) {
      formData.append('canvas_edit_file', canvasEdit.blob, canvasEdit.filename || 'canvas_edit.xlsx');
      formData.append('canvas_edit_metadata', JSON.stringify(canvasEdit.metadata));
    }
    body = formData; // multipart sets its own boundary — no Content-Type here
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ user_message: userMessage, client_message_id: clientMessageId });
  }

  let response;
  try {
    response = await fetch(url, { method: 'POST', headers, body, signal });
  } catch (error) {
    // Deliberate aborts are not errors — resolve, never reject with AbortError.
    if (signal?.aborted) return { outcome: 'aborted' };
    throw error;
  }

  if (response.status === 409) {
    let retryAfter = null;
    try {
      const busyBody = await response.json();
      retryAfter = busyBody?.retry_after ?? null;
    } catch (parseError) {
      // No parseable body — the snackbar just loses its timing hint.
    }
    throw new BusyError(retryAfter);
  }

  if (!response.ok) {
    let bodyText = '';
    try {
      bodyText = await response.text();
    } catch (readError) {
      // Body unreadable — status alone carries the error.
    }
    throw new TurnRequestError(response.status, bodyText);
  }

  // A retried client_message_id whose turn already completed returns the
  // stored TurnResult as plain JSON — detect by Content-Type (§7.4).
  const contentType = response.headers.get('Content-Type') || '';
  if (!contentType.includes('text/event-stream')) {
    return { outcome: 'replayed', result: await response.json() };
  }

  // v1's reader loop: decode, buffer, split on '\n\n', 'data:' prefix.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data:')) {
          const jsonStr = line.substring(5).trim();
          onEvent(JSON.parse(jsonStr));
        }
      }
    }
  } catch (error) {
    if (signal?.aborted) return { outcome: 'aborted' };
    throw error;
  }

  if (signal?.aborted) return { outcome: 'aborted' };
  return { outcome: 'streamed' };
}

// A chat's approval records requested inside a span of time. Either end may be
// null, meaning that side is open. A range on the one field `requested_at`
// needs no composite index.
const approvalsRequestedBetween = (companyId, agentId, requestedSince, requestedBefore) => {
  const approvalsRef = collection(db, 'users', companyId, 'agents', agentId, 'pending_approvals');
  const bounds = [];
  if (requestedSince != null) bounds.push(where('requested_at', '>=', requestedSince));
  if (requestedBefore != null) bounds.push(where('requested_at', '<', requestedBefore));
  return query(approvalsRef, ...bounds);
};

const approvalRecords = (snapshot) => snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));

/**
 * Subscribe to an Agent's durable parked-approval records.
 *
 * Documents live at
 * `users/{companyId}/agents/{agentId}/pending_approvals/{agent_message_id}_{cycle}`
 * and are delivered exactly as persisted — the same record shape the live
 * `approval_required` event carries — so a reconnect, a reload, or a second
 * browser tab reconstructs an outstanding approval in full instead of degrading
 * to a card with missing detail. Callers hydrate them through the shared
 * approval hydrator.
 *
 * A record is never deleted when its approval completes, and each one carries
 * the parked cycle's full provider content, so a long-lived chat holds many
 * large ones. `requestedSince` (a Date, or null for no bound) keeps the
 * subscription to the approvals of the messages the page has loaded (bug 15.4).
 *
 * onRecords receives an array of `{ id, ...documentData }`. Returns the
 * unsubscribe function.
 */
export function subscribePendingApprovals(companyId, agentId, requestedSince, onRecords, onError) {
  const approvals = approvalsRequestedBetween(companyId, agentId, requestedSince, null);
  return onSnapshot(approvals, (snapshot) => {
    onRecords(approvalRecords(snapshot));
  }, (error) => {
    console.error('Error listening to pending approvals:', error);
    onError?.(error);
  });
}

/**
 * Read once the approval records requested from `requestedSince` (a Date, or
 * null for no lower bound) up to, but not including, `requestedBefore`: the
 * approvals of one page of older messages. Resolves to the same
 * `{ id, ...documentData }` records the subscription delivers.
 */
export async function fetchPendingApprovals(companyId, agentId, requestedSince, requestedBefore) {
  const snapshot = await getDocs(approvalsRequestedBetween(companyId, agentId, requestedSince, requestedBefore));
  return approvalRecords(snapshot);
}

/**
 * Submit a ONE-OFF approval decision for a parked cycle and consume whatever
 * the backend returns.
 *
 * The parked approval is addressed by `approval_id = "{messageId}_{cycle}"`
 * (backend store contract) — the `agent_message_id` and `cycle` come straight
 * off the persisted record. `decisions` maps each tool_use_id to `'approved'`
 * or `'denied'`, and carries ONLY calls whose original gate is `ask`: `allow`
 * and `deny` calls are durable for replay and are never decided by the user.
 * Approvals are strictly one-off — the body is exactly `{ decisions }`, with
 * deliberately no `always_allow` or any other persistent-grant field.
 *
 * Resolves:
 *   { outcome: 'streamed' }        — the final decision resumed the turn and
 *                                    its SSE stream closed normally
 *   { outcome: 'json', result }    — a non-stream 200: a still-pending partial
 *                                    batch ({status:'pending'}) or a replayed
 *                                    terminal result
 *   { outcome: 'aborted' }         — the signal fired (deliberate, never an error)
 * Throws: TurnRequestError (non-OK) | network errors.
 *
 * onEvent(parsedEventData) is called for every SSE data line of the resumed
 * turn (identical contract to streamTurn); unknown event types pass through.
 */
export async function decideApproval({
  agentId,
  messageId,
  cycle,
  decisions,
  signal,
  onEvent,
}) {
  const approvalId = `${messageId}_${cycle}`;
  const url = `${serviceBaseUrl()}/v2/agents/${agentId}/approvals/${approvalId}`;

  const idToken = await mainAuth.currentUser.getIdToken();
  const emulatedOrg = sessionStorage.getItem('emulatedOrganisation');
  const headers = { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' };
  if (emulatedOrg) headers['X-Emulated-Organisation'] = emulatedOrg;

  // One-off approval only: the request body is exactly { decisions } — no
  // always_allow, no persistent grant. The backend scopes the decision to this
  // parked cycle/tool-use and resumes or cancels that cycle alone.
  const body = JSON.stringify({ decisions });

  let response;
  try {
    response = await fetch(url, { method: 'POST', headers, body, signal });
  } catch (error) {
    if (signal?.aborted) return { outcome: 'aborted' };
    throw error;
  }

  if (!response.ok) {
    let bodyText = '';
    try {
      bodyText = await response.text();
    } catch (readError) {
      // Body unreadable — status alone carries the error.
    }
    throw new TurnRequestError(response.status, bodyText);
  }

  // A still-pending partial batch, or a replayed terminal result, comes back as
  // plain JSON; only the final decision that resumes the turn streams SSE.
  const contentType = response.headers.get('Content-Type') || '';
  if (!contentType.includes('text/event-stream')) {
    return { outcome: 'json', result: await response.json() };
  }

  // Same reader loop as streamTurn: decode, buffer, split on '\n\n', 'data:'.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data:')) {
          const jsonStr = line.substring(5).trim();
          onEvent?.(JSON.parse(jsonStr));
        }
      }
    }
  } catch (error) {
    if (signal?.aborted) return { outcome: 'aborted' };
    throw error;
  }

  if (signal?.aborted) return { outcome: 'aborted' };
  return { outcome: 'streamed' };
}
